import React, { useState, useEffect, useRef } from 'react';
import {
    Send,
    RotateCcw,
    History,
    Paperclip,
    AtSign,
    FileText,
    Copy,
    ThumbsUp,
    ThumbsDown,
    Check,
    ExternalLink,
    ChevronRight,
    ShieldCheck,
    Sparkles,
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import ArtifactRenderer from './artifacts/ArtifactRenderer';
import ThreadHistoryPanel from './ThreadHistoryPanel';
import MemoryProposalCard from './MemoryProposalCard';
import { useCapabilities } from '../../../context/CapabilityContext';
import { useFeatures } from '../../../context/FeatureContext';
import useCreditRefreshAfterChat from '../../../hooks/useCreditRefreshAfterChat';
import { io } from 'socket.io-client';
import { useParams, useHistory, useLocation, Link } from 'react-router-dom';
import { readHandOff, editableTaskQuestion, taskReturnPath } from '../../../context/TaskContextDescriptor';
import axios from '../../../services/AxiosInstance';
// The RAW client for the refresh call: the shared instance's 401 interceptor refreshes and
// retries, which is exactly the loop we must not enter from inside a refresh.
import rawAxios from 'axios';
import { getApiUrl } from '../../../utils/ApiUtils';
import { store } from '../../../store/store';
import { setAccessToken } from '../../../store/actions/AuthActions';
import { refreshChatDelegation, DELEGATION_REFRESHED } from '../../../services/ChatDelegation';
import { newAiOperationId } from '../../../services/AiOperation';

// Generate stable user ID
// Agent cartoon avatars (served from public/, same assets as the Meet-the-team view).
const AGENT_AVATAR_BASE = `${process.env.PUBLIC_URL || ''}/agent-avatars`;
const FINN_AVATAR = `${AGENT_AVATAR_BASE}/finn.svg`;
const avatarForHandle = (handle) => `${AGENT_AVATAR_BASE}/${String(handle || '@finn').replace(/^@/, '').toLowerCase()}.svg`;
const finnAvatarImg = { width: '100%', height: '100%', borderRadius: 'inherit', objectFit: 'cover', display: 'block' };

// Built-ins ship a pre-generated cartoon SVG; custom agents (no asset) get a gradient initial.
const BUILTIN_AVATAR_HANDLES = new Set(['@finn', '@cfo', '@paige', '@reece', '@harper', '@cruz', '@vera', '@mats', '@sage', '@quinn']);
const avatarHash = (s) => [...String(s)].reduce((a, c) => (a * 31 + c.charCodeAt(0)) >>> 0, 7);

const BotAvatar = ({ agent }) => {
    const handle = (agent?.handle || '@finn').toLowerCase();
    const name = agent?.displayName || 'Finn';
    if (BUILTIN_AVATAR_HANDLES.has(handle)) {
        return <img src={avatarForHandle(handle)} alt={name} style={finnAvatarImg} onError={(e) => { e.currentTarget.src = FINN_AVATAR; }} />;
    }
    const h = avatarHash(handle) % 360;
    const id = `cbav-${handle.replace(/[^a-z0-9]/g, '')}`;
    return (
        <svg viewBox="0 0 48 48" style={{ width: '100%', height: '100%', display: 'block', borderRadius: 'inherit' }} aria-hidden="true">
            <defs><linearGradient id={id} x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stopColor={`hsl(${h}, 70%, 58%)`} /><stop offset="100%" stopColor={`hsl(${(h + 38) % 360}, 72%, 44%)`} /></linearGradient></defs>
            <rect width="48" height="48" rx="13" fill={`url(#${id})`} />
            <text x="24" y="25" textAnchor="middle" dominantBaseline="central" fontSize="22" fontWeight="700" fill="#fff">{name.charAt(0).toUpperCase()}</text>
        </svg>
    );
};

// A typed @mention rendered bolder/light-blue, with a hover bubble (role + blurb).
const MentionChip = ({ agent, raw }) => (
    <span className="finn-mention-chip">
        {raw}
        <span className="finn-mention-tip">
            <span className="finn-mention-tip-name">{agent.displayName}{agent.role ? ` · ${agent.role}` : ''}</span>
            {agent.blurb && <span className="finn-mention-tip-blurb">{agent.blurb}</span>}
        </span>
    </span>
);

// Split text into plain strings + recognized @mentions, preserving the text EXACTLY (so the
// input backdrop can overlay the textarea character-for-character).
const mentionParts = (text, roster) => {
    const byHandle = new Map((roster || []).map((a) => [String(a.handle).toLowerCase(), a]));
    const re = /@[a-zA-Z0-9-]+/g;
    const parts = [];
    let last = 0;
    let m;
    while ((m = re.exec(text)) !== null) {
        const agent = byHandle.get(m[0].toLowerCase());
        if (!agent) continue;
        if (m.index > last) parts.push({ text: text.slice(last, m.index) });
        parts.push({ agent, raw: m[0] });
        last = m.index + m[0].length;
    }
    if (last < text.length) parts.push({ text: text.slice(last) });
    return parts;
};

// Sent messages: known @handles → MentionChip (bolder blue + hover bubble). Unknown stay text.
const renderWithMentions = (text, roster) => {
    if (!text) return text;
    const parts = mentionParts(text, roster);
    if (!parts.some((p) => p.agent)) return text;
    return parts.map((p, i) => (p.agent ? <MentionChip key={i} agent={p.agent} raw={p.raw} /> : p.text));
};

// Live input backdrop: same split, light-blue spans (faux-bold via text-shadow so the token keeps
// the same width as the textarea's text → the caret stays aligned). Trailing newline gets a pad so
// the final empty line renders. pointer-events: none (the textarea above owns interaction).
const renderInlineMentions = (text, roster) => {
    const safe = text?.endsWith('\n') ? `${text}​` : (text || '');
    return mentionParts(safe, roster).map((p, i) =>
        p.agent ? <span key={i} className="finn-mention-inline">{p.raw}</span> : <span key={i}>{p.text}</span>,
    );
};

// Thread-history panel visibility. Desktop: open by default, with the user's
// last hide/show choice persisted. Mobile (<768px, where the panel is a
// covering overlay — see ThreadHistoryPanel's media query): closed by default,
// opens are transient and never persisted.
const HISTORY_PREF_KEY = 'finn_history_open';
const isMobileViewport = () => window.matchMedia('(max-width: 768px)').matches;
const initialHistoryOpen = () => {
    if (isMobileViewport()) return false;
    try {
        return localStorage.getItem(HISTORY_PREF_KEY) !== 'false';
    } catch {
        return true;
    }
};

const generateUserId = (companyId) => {
    const storageKey = `ledgy_chat_user_${companyId}`;
    let userId = localStorage.getItem(storageKey);
    if (!userId) {
        userId = `user-${companyId}-${Date.now()}-${Math.random().toString(36).substring(2, 15)}`;
        localStorage.setItem(storageKey, userId);
    }
    return userId;
};

/**
 * Extract source references from response text.
 * Looks for KÄLLOR: sections or inline URLs.
 */
function extractSources(text) {
    const sources = [];
    // Match "KÄLLOR:" section
    const kallorMatch = text.match(/KÄLLOR:\s*\n([\s\S]*?)(?=\n\n[A-ZÅÄÖ]|\n*$)/);
    if (kallorMatch) {
        const lines = kallorMatch[1].split('\n').filter(l => l.trim().startsWith('-'));
        for (const line of lines) {
            const urlMatch = line.match(/(https?:\/\/[^\s)]+)/);
            const label = line.replace(/^-\s*/, '').replace(urlMatch ? urlMatch[0] : '', '').replace(/:\s*$/, '').trim();
            sources.push({
                label: label || (urlMatch ? new URL(urlMatch[1]).hostname : 'Källa'),
                url: urlMatch ? urlMatch[1] : null,
            });
        }
    }
    // Also find inline URLs not in KÄLLOR section
    const urlRegex = /https?:\/\/(?:www\.)?(?:skatteverket|bfn|riksdagen|bolagsverket|forsakringskassan)\.[a-z]+[^\s)"]*/gi;
    const inlineUrls = text.match(urlRegex) || [];
    for (const url of inlineUrls) {
        if (!sources.some(s => s.url === url)) {
            try {
                sources.push({ label: new URL(url).hostname.replace('www.', ''), url });
            } catch { /* skip invalid */ }
        }
    }
    return sources;
}

/**
 * Strip the KÄLLOR section from displayed text (we show it separately).
 */
function stripSourcesSection(text) {
    return text.replace(/\n*KÄLLOR:\s*\n[\s\S]*?(?=\n\n[A-ZÅÄÖ]|\s*$)/, '').trim();
}

const BookkeepingChatPage = () => {
    const { companyId } = useParams();
    const { refreshCredits } = useFeatures();
    const refreshCreditsAfterChat = useCreditRefreshAfterChat(companyId, refreshCredits);
    // Confirming a COMPANY-scoped memory needs an Owner/Admin; the card reads this to decide
    // whether to offer the buttons or point at Vad Finn vet instead.
    const { capabilities } = useCapabilities();
    const isMemoryAdmin = Boolean(capabilities?.isAdmin || capabilities?.isOwner);
    const history = useHistory();
    const location = useLocation();
    // A message handed over from the Översikt composer (DashboardWelcome).
    // Captured in a ref at mount so clearing the router state below can't
    // re-trigger it, and so a re-render never resends.
    const pendingInitialRef = useRef(location.state?.initialMessage || null);
    /**
     * TASK CONTEXT (docs/TASK_CONTEXT_REGISTRY.md §7.2) — the transaction (or other entity) the
     * user was looking at when they came here.
     *
     * Captured in a ref at MOUNT and then held in state, for the same reason `initialMessage` is:
     * clearing the router state below must not un-adopt it, and a re-render must not re-adopt a
     * descriptor the user has since dismissed. Re-validated by `readHandOff` — router state
     * survives history navigation and is not a trusted channel.
     *
     * Absent — arriving at /ai-assistant directly — is the ordinary case and stays exactly as it
     * is today: no descriptor is sent, the gateway resolves the `unknown` surface, and the run
     * gets the minimal contract.
     */
    const handOffRef = useRef(readHandOff(location.state));
    const [taskContext, setTaskContext] = useState(() => handOffRef.current);
    /**
     * The socket's task-context delegation has expired and could not be refreshed automatically.
     *
     * A chat socket authenticates once at the handshake and lives as long as the browser tab; the
     * access token it presented lasts fifteen minutes. Chat itself is unaffected — its identity was
     * established at connect — but task-context capture forwards that assertion on every run, so
     * past minute fifteen a contextual turn takes a guaranteed 401. The gateway makes the state
     * STICKY and tells us once per expiry; leaving it unhandled would mean that in render mode
     * every contextual turn aborts for a reason the user cannot see or act on.
     */
    const [delegationExpired, setDelegationExpired] = useState(false);
    const [messages, setMessages] = useState([]);
    const [inputMessage, setInputMessage] = useState(() => editableTaskQuestion(location.state));
    const taskReturnRef = useRef(taskReturnPath(location.state));
    const [agentRoster, setAgentRoster] = useState([]);
    const [mention, setMention] = useState({ open: false, items: [], index: 0 });
    const [activeAgent, setActiveAgent] = useState(null); // the sticky specialist (null = Finn)
    const [isConnected, setIsConnected] = useState(false);
    const [isTyping, setIsTyping] = useState(false);
    // The live activity trail for the turn in flight (agents: run/activity.ts → chat lane).
    // Steps arrive as they happen and are shown under the typing dots; the list is cleared when
    // the answer lands, because it describes work in progress, not history worth keeping.
    const [activity, setActivity] = useState([]);
    // Opening the page shows an EMPTY transcript, but the gateway continues whatever thread the
    // stored session points at (socketHandler.threadKeyFor → session.threadEpoch). So without
    // this, a user who opens "AI-assistent" and types is appending to the previous conversation
    // while looking at a blank one: the model answers with context the user cannot see, and the
    // whole old history is re-sent (and re-billed) on every turn. Resume it once per mount so
    // what is on screen IS the conversation being continued; "Ny konversation" is how you get a
    // fresh one.
    const autoResumedRef = useRef(false);
    const [socket, setSocket] = useState(null);
    const [attachments, setAttachments] = useState([]);
    const [copiedId, setCopiedId] = useState(null);
    const [threads, setThreads] = useState([]);
    const [historyOpen, setHistoryOpen] = useState(initialHistoryOpen);

    const messagesEndRef = useRef(null);
    const textareaRef = useRef(null);
    const highlightRef = useRef(null); // backdrop that highlights @mentions under the textarea
    const syncHighlightScroll = () => {
        if (highlightRef.current && textareaRef.current) {
            highlightRef.current.scrollTop = textareaRef.current.scrollTop;
            highlightRef.current.scrollLeft = textareaRef.current.scrollLeft;
        }
    };
    const fileInputRef = useRef(null);
    const pageRef = useRef(null);

    // The page is a flex column that fills the viewport below its own top edge; the
    // messages area scrolls INTERNALLY and the input sits in normal flow at the bottom.
    // Width therefore always follows .content-body — no fixed positioning, no JS sidebar
    // measurement (the old fixed-input + margin-left snapshot went stale on sidebar
    // collapse, which doesn't fire a window resize). Only the VERTICAL offset is
    // measured, and vertical geometry never changes with the sidebar.
    useEffect(() => {
        const footer = document.querySelector('.footer');
        if (footer) footer.style.display = 'none';

        // This route normally renders full-bleed (jsx/index.js skips the
        // container-md wrapper + its inline minHeight for ai-assistant). If it
        // ever gets re-wrapped, neutralize the minHeight — with a viewport-fitted
        // chat it only adds dead scroll space below the input.
        const container = pageRef.current?.closest('.container-md');
        const prevMinHeight = container ? container.style.minHeight : null;
        if (container) container.style.minHeight = 'auto';

        const sizeToViewport = () => {
            if (!pageRef.current) return;
            const top = Math.max(0, Math.round(pageRef.current.getBoundingClientRect().top + window.scrollY));
            pageRef.current.style.height = `calc(100vh - ${top}px)`;
        };
        sizeToViewport();
        window.addEventListener('resize', sizeToViewport);

        return () => {
            if (footer) footer.style.display = '';
            if (container) container.style.minHeight = prevMinHeight;
            window.removeEventListener('resize', sizeToViewport);
        };
    }, []);

    const userIdRef = useRef(generateUserId(companyId));
    const userId = userIdRef.current;
    const lastConnErrorRef = useRef(null); // dedupe repeated connect_error system messages
    const msgSeqRef = useRef(0); // Date.now() alone collides when two messages land in the same ms
    const copyTimerRef = useRef(null);
    // The text of the turn currently in flight, for restoring it if the turn is refused.
    const lastSentTextRef = useRef('');

    // One example per corner of the team's turf (bokföring / kundreskontra /
    // dataanalys / rådgivning) — the welcome should show breadth, not four
    // variants of "bokför detta". Grounded in the persona blurbs in
    // ledgerloop-agents/src/registry/personas.ts.
    const quickStartExamples = [
        { message: "Jag köpte kontorsmaterial för 450 kr med företagskortet" },
        { message: "Vilka kundfakturor är fortfarande obetalda?" },
        { message: "Hur gick förra månaden – omsättning och resultat?" },
        { message: "Är representation avdragsgillt?" },
    ];

    // Initialize socket connection
    useEffect(() => {
        // auth as a callback so every (re)connection handshake reads the CURRENT
        // access token — a static object would replay the mount-time token after
        // a refresh and get rejected by the gateway.
        const newSocket = io(process.env.REACT_APP_CHAT_URL || 'http://localhost:3004', {
            withCredentials: true,
            auth: (cb) => cb({ token: store.getState().auth.accessToken, userId, companyId })
        });

        newSocket.on('connect', () => {
            setIsConnected(true);
            lastConnErrorRef.current = null;
            // Ask for the thread list immediately: the response is what tells us WHICH thread the
            // gateway is about to continue, and that has to be known before the first message —
            // not after the reply comes back, which is when the list was previously refreshed.
            try { newSocket.emit('listThreads'); } catch { /* non-fatal */ }
        });

        newSocket.on('disconnect', () => {
            setIsConnected(false);
            // A reply emitted while we're offline is lost (no delivery buffering),
            // so don't leave the typing dots spinning forever.
            setIsTyping(false);
            setActivity([]);
        });

        // The gateway rejects unauthorized handshakes (fail-closed company-access check)
        // with an Error whose message surfaces here — show it instead of a silent offline
        // dot. Deduped: socket.io retries the handshake indefinitely and would otherwise
        // append the same system message on every attempt.
        newSocket.on('connect_error', (err) => {
            setIsConnected(false);
            if (
                err?.message &&
                !/xhr poll error|websocket error/i.test(err.message) &&
                lastConnErrorRef.current !== err.message
            ) {
                lastConnErrorRef.current = err.message;
                addMessage('system', err.message);
            }
        });

        newSocket.on('chatResponse', (data) => {
            refreshCreditsAfterChat();
            setIsTyping(false);
            setActivity([]);
            addMessage('bot', data.response, false, {
                verification: data.verification,
                sources: data.sources,
                artifacts: data.artifacts,
                suggestions: data.suggestions,
                requiresMoreInfo: data.requiresMoreInfo,
                // Agent platform (ledgerloop-agents gateway) extensions:
                // proactive first-message alerts + staged HITL approvals.
                alerts: data.alerts,
                requiresApproval: data.requiresApproval,
                approvalId: data.approvalId,
                // Who answered (P3): persona for the bubble avatar/name.
                agent: data.agent
            });
            // Track the active specialist (sticky); null when Finn answered.
            setActiveAgent(data.agent && data.agent.name && data.agent.name !== 'cfo' ? data.agent : null);
            // Keep the (now default-visible) thread panel fresh: the gateway
            // updates the thread row on each turn (title, lastMessageAt) but
            // only pushes threadList after explicit thread actions.
            try { newSocket.emit('listThreads'); } catch { /* non-fatal */ }
        });

        newSocket.on('chatError', (error) => {
            refreshCreditsAfterChat();
            setIsTyping(false);
            setActivity([]);
            // The gateway refused a capture-enabled turn (docs/TASK_CONTEXT_REGISTRY.md §7.2):
            // the delegation, the tenancy or the contract did not check out, and a run must not
            // proceed as though it had read the row the user pointed at. Dropping the descriptor
            // makes the retry an ordinary entity-less question rather than a loop of the same
            // refusal — the user keeps the assistant, and loses only the attached transaction.
            if (error?.taskContextAborted) {
                setTaskContext(null);
                // Hand the question back rather than describing a retry that does not happen. The
                // composer was cleared optimistically when the turn was sent, so without this the
                // user has to retype it — and the descriptor has just been dropped, so sending it
                // again IS the "ask without context" the message suggests.
                if (lastSentTextRef.current) setInputMessage(lastSentTextRef.current);
                addMessage(
                    'bot',
                    `${error.error} Din fråga står kvar i rutan nedan — skicka den igen så svarar jag utan den valda posten.`,
                    true,
                );
                return;
            }
            addMessage('bot', error.error + (error.details ? `: ${error.details}` : ''), true);
        });

        /**
         * The socket's delegation aged out. Refresh it in place, without dropping the conversation.
         *
         * The state machine lives in services/ChatDelegation.js — refresh, persist, re-present,
         * interpret the acknowledgement — so it can be tested without mounting this page. All this
         * handler decides is what the user is told when it does not work.
         */
        newSocket.on('chatSessionExpired', async () => {
            const attempt = () => refreshChatDelegation({
                refresh: async () => {
                    const { data } = await rawAxios.post(
                        getApiUrl('auth/refresh'), {}, { withCredentials: true },
                    );
                    return data;
                },
                // The socket's auth callback reads the store on every (re)connection, so a stale
                // store would replay the expired token the moment the socket drops.
                persist: (accessToken) => store.dispatch(setAccessToken(accessToken)),
                present: (accessToken) => new Promise((resolve) => {
                    newSocket.emit('reauthenticate', { token: accessToken }, resolve);
                }),
            });

            // A lost acknowledgement is retried INSIDE `refreshChatDelegation`, against the token
            // it already holds — retrying from here would repeat the HTTP refresh to obtain a
            // token we are still holding, which is work for nothing and would break if the backend
            // ever started rotating refresh tokens.
            const result = await attempt();
            if (result.outcome === DELEGATION_REFRESHED) {
                setDelegationExpired(false);
                return;
            }
            // Nothing in this tab will fix it, so say so rather than retrying into the same wall.
            console.warn('[chat] delegation refresh failed:', result.outcome, result.reason);
            setDelegationExpired(true);
        });

        newSocket.on('conversationReset', (data) => {
            addMessage('system', data.message);
        });

        // Phase 4: Finn proposed remembering something from this conversation. Nothing is
        // saved yet — the chip links to Vad Finn vet where the user confirms or rejects.
        // What the agent is doing right now: handoffs and tool calls, in order. Names and
        // categories only — the server never puts tool arguments on this channel — so nothing
        // here needs the redaction the transcript would.
        newSocket.on('agentActivity', (event) => {
            if (!event?.label) return;
            setActivity((steps) => (
                steps.some((s) => s.step === event.step) ? steps : [...steps, event]
            ));
        });

        newSocket.on('memoryProposed', (data) => {
            const proposals = (data?.proposals ?? []).filter((p) => p?.id && p?.displayText);
            if (proposals.length === 0) return;
            // Decided HERE, in the conversation that produced it — see MemoryProposalCard. The
            // text carries no message of its own: the card renders the proposal's own wording,
            // because that is the sentence a confirm actually stores.
            addMessage('system', '', false, { kind: 'memory-proposal', proposals });
        });

        // ── Thread history (ChatGPT-style list + resume) ──
        newSocket.on('threadList', (data) => {
            const list = Array.isArray(data?.threads) ? data.threads : [];
            setThreads(list);
            if (!autoResumedRef.current) {
                autoResumedRef.current = true;
                if (handOffRef.current) {
                    // Arrived from a product surface ("Fråga assistenten" on a bank row): the
                    // descriptor IS a new subject, so this turn gets its own conversation. Resuming
                    // an old one would answer about this transaction inside a thread about
                    // something else — the previous history colours the answer, the thread's title
                    // stops describing it, and the whole old transcript is re-sent every turn.
                    // The raw event, not startNewThread(): that helper clears the descriptor, which
                    // is the one thing this turn must keep.
                    newSocket.emit('newThread');
                    return;
                }
                // Otherwise: show the thread the gateway will actually continue. Only when it HAS
                // messages — a fresh epoch has nothing to show and resuming it is a wasted trip.
                const active = list.find((t) => t.active && t.messageCount > 0);
                if (active) newSocket.emit('resumeThread', { epoch: active.epoch });
            }
        });

        // Resuming replaces the transcript with the stored history. The gateway sends
        // optional per-message timestamp (ISO, superstep precision) and artifacts —
        // mapped into the same message model live chatResponse bubbles use; older
        // gateways omit them and the bubbles skip the clock/artifacts as before.
        newSocket.on('threadResumed', (data) => {
            if (!data || typeof data.epoch !== 'number') return;
            setIsTyping(false);
            setActivity([]);
            setActiveAgent(null);
            setMessages((data.messages || []).map((m, i) => ({
                id: `hist-${data.epoch}-${i}`,
                sender: m.role === 'user' ? 'user' : 'bot',
                message: m.content,
                timestamp: m.timestamp ? new Date(m.timestamp) : null,
                error: false,
                agent: m.agent,
                artifacts: m.artifacts,
            })));
        });

        setSocket(newSocket);
        setTimeout(() => textareaRef.current?.focus(), 100);

        return () => { newSocket.close(); };
    }, [companyId]);

    // Auto scroll
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages, isTyping]);

    // Auto-resize textarea (+ keep the highlight backdrop scroll-aligned)
    useEffect(() => {
        if (textareaRef.current) {
            textareaRef.current.style.height = 'auto';
            textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 150) + 'px';
        }
        syncHighlightScroll();
    }, [inputMessage]);

    const addMessage = (sender, message, error = false, extra = {}) => {
        msgSeqRef.current += 1;
        setMessages(prev => [...prev, {
            id: `m-${Date.now()}-${msgSeqRef.current}`,
            sender,
            message,
            timestamp: new Date(),
            error,
            ...extra
        }]);
    };

    // `overrideText` is for programmatic sends (the Översikt hand-off), where
    // the text never passes through inputMessage — setting state and calling
    // this in the same tick would read the stale value.
    const sendMessage = async (overrideText) => {
        const text = (typeof overrideText === 'string' ? overrideText : inputMessage).trim();
        if ((!text && attachments.length === 0) || !socket || !isConnected) return;

        addMessage('user', text || '(bild bifogad)');
        // Kept so a refused turn can be handed back to the user instead of vanishing: `sendMessage`
        // clears the composer optimistically, and a capture abort happens after that.
        lastSentTextRef.current = text;
        setInputMessage('');
        setIsTyping(true);
        setActivity([]);

        // Optimistic attribution: if the user addressed an agent (@handle), show them as the one
        // "thinking" right away instead of Finn. @finn → back to Finn. Resolved against the roster.
        const handleMatch = text.match(/^\s*@([a-zA-Z0-9-]+)/);
        if (handleMatch) {
            const h = `@${handleMatch[1].toLowerCase()}`;
            if (h === '@finn' || h === '@cfo') {
                setActiveAgent(null);
            } else {
                const hit = agentRoster.find((a) => String(a.handle).toLowerCase() === h);
                if (hit) setActiveAgent({ displayName: hit.displayName, handle: hit.handle });
            }
        }

        const encodedAttachments = await Promise.all(
            attachments.map(att => new Promise((resolve) => {
                const reader = new FileReader();
                reader.onload = () => {
                    const base64 = reader.result.split(',')[1];
                    resolve({ filename: att.name, mimeType: att.type, data: base64 });
                };
                reader.onerror = () => resolve(null);
                reader.readAsDataURL(att.file);
            }))
        );

        socket.emit('chatMessage', {
            message: text,
            operationId: newAiOperationId(),
            attachments: encodedAttachments.filter(Boolean),
            // The typed descriptor, when one is attached. Exactly the four wire fields — the
            // display label is dropped by `readHandOff`'s caller and never sent. A HINT: the
            // gateway validates it against the closed registry and the backend authorizes the
            // read independently, so this grants nothing.
            ...(taskContext && {
                task: {
                    surface: taskContext.surface,
                    entityType: taskContext.entityType,
                    entityId: taskContext.entityId,
                    intent: taskContext.intent,
                },
            }),
        });
        setAttachments([]);
    };

    // The hand-off is consumed once. Clearing the router state here means a refresh, or a
    // back-then-forward, does not silently re-attach a transaction the user has moved on from —
    // the same discipline the Översikt message hand-off uses one effect below.
    useEffect(() => {
        if (!handOffRef.current) return;
        history.replace(location.pathname);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Leaving the surface clears the context. Nothing persists it, so an unmount is the whole
    // implementation — stated explicitly because "cleared when the user leaves" is a contract,
    // not an accident of how React happens to unmount state.
    useEffect(() => () => setTaskContext(null), []);

    // Hand-off from the Översikt composer: send as soon as the socket is up.
    // The router state is cleared first so a refresh or a back-then-forward
    // doesn't replay the question, and the ref is nulled before the send so a
    // reconnect (which re-fires this effect) can't send it twice.
    useEffect(() => {
        if (!socket || !isConnected || !pendingInitialRef.current) return;
        const text = pendingInitialRef.current;
        pendingInitialRef.current = null;
        history.replace(location.pathname);
        sendMessage(text);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [socket, isConnected]);

    // Best-effort thread emits: history is an enhancement — a socket hiccup
    // must never take down the chat itself.
    const emitThreadEvent = (event, payload) => {
        if (!socket || !isConnected) return false;
        try {
            if (payload !== undefined) socket.emit(event, payload);
            else socket.emit(event);
            return true;
        } catch (err) {
            console.error(`Failed to emit ${event}:`, err);
            return false;
        }
    };

    // Hide/show the thread panel. The desktop preference persists across
    // visits; mobile overlay toggles are transient by design.
    const toggleHistory = () => {
        setHistoryOpen((open) => {
            const next = !open;
            if (!isMobileViewport()) {
                try {
                    localStorage.setItem(HISTORY_PREF_KEY, String(next));
                } catch { /* private mode etc — just don't persist */ }
            }
            return next;
        });
    };

    // Crossing the breakpoint: on mobile the open panel becomes a covering
    // overlay — close it; back on desktop, restore the persisted preference.
    useEffect(() => {
        const mq = window.matchMedia('(max-width: 768px)');
        const onChange = (e) => setHistoryOpen(e.matches ? false : initialHistoryOpen());
        if (mq.addEventListener) mq.addEventListener('change', onChange);
        else mq.addListener(onChange); // Safari < 14
        return () => {
            if (mq.removeEventListener) mq.removeEventListener('change', onChange);
            else mq.removeListener(onChange);
        };
    }, []);

    // On mobile the panel overlays the chat, so acting on it should reveal
    // the conversation again.
    const closeHistoryOnMobile = () => {
        if (isMobileViewport()) setHistoryOpen(false);
    };

    // "Ny konversation" — the server rotates to a fresh thread and answers with
    // an updated threadList. Replaces the old resetConversation emit (the
    // gateway's newThread supersedes it; nothing else references it).
    const startNewThread = () => {
        if (emitThreadEvent('newThread')) {
            setMessages([]);
            setActiveAgent(null);
            setIsTyping(false);
            setActivity([]);
            // A new conversation is a new subject. Carrying the previous thread's transaction into
            // it would attach context to questions that are not about it.
            setTaskContext(null);
            closeHistoryOnMobile();
        }
    };

    const resumeThread = (epoch) => {
        emitThreadEvent('resumeThread', { epoch });
        // Same reasoning as a new thread: the resumed conversation was not about this row.
        setTaskContext(null);
        closeHistoryOnMobile();
    };

    const renameThread = (epoch, title) => {
        emitThreadEvent('renameThread', { epoch, title });
    };

    const archiveThread = (epoch) => {
        emitThreadEvent('archiveThread', { epoch });
    };

    // Refresh the list whenever the history panel is opened (and re-fetch if
    // the socket reconnects while it is open).
    useEffect(() => {
        if (!historyOpen || !socket || !isConnected) return;
        try {
            socket.emit('listThreads');
        } catch (err) {
            console.error('Failed to emit listThreads:', err);
        }
    }, [historyOpen, socket, isConnected]);

    // Load the @-roster (built-ins + this tenant's custom agents) for autocomplete. Best-effort.
    useEffect(() => {
        if (!companyId) return undefined;
        let active = true;
        axios.get(getApiUrl(`${companyId}/agent-roster`), { withCredentials: true })
            .then(({ data }) => { if (active && Array.isArray(data)) setAgentRoster(data); })
            .catch(() => {});
        return () => { active = false; };
    }, [companyId]);

    // @mention autocomplete: match a trailing "@partial" at the end of the input.
    const MENTION_RE = /(^|\s)@([a-zA-Z0-9-]*)$/;
    const refreshMention = (value) => {
        const m = MENTION_RE.exec(value);
        if (!m) { setMention((s) => (s.open ? { open: false, items: [], index: 0 } : s)); return; }
        const q = m[2].toLowerCase();
        const items = agentRoster.filter((a) => {
            const h = String(a.handle || '').replace(/^@/, '').toLowerCase();
            return h.startsWith(q) || String(a.displayName || '').toLowerCase().includes(q) || String(a.role || '').toLowerCase().includes(q);
        }).slice(0, 8);
        setMention({ open: items.length > 0, items, index: 0 });
    };

    const handleInputChange = (e) => { const v = e.target.value; setInputMessage(v); refreshMention(v); };

    // The "@" button: insert a mention trigger and open the roster (discoverability — so users
    // know they can address a specialist without first guessing they can type "@").
    const openMentionMenu = () => {
        if (agentRoster.length === 0) return;
        const sep = (inputMessage === '' || /\s$/.test(inputMessage)) ? '' : ' ';
        const next = `${inputMessage}${sep}@`;
        setInputMessage(next);
        refreshMention(next);
        setTimeout(() => textareaRef.current?.focus(), 0);
    };

    const selectMention = (item) => {
        setInputMessage((prev) => prev.replace(MENTION_RE, (full, pre) => `${pre}${item.handle} `));
        setMention({ open: false, items: [], index: 0 });
        setTimeout(() => textareaRef.current?.focus(), 0);
    };

    const handleKeyDown = (e) => {
        if (mention.open && mention.items.length > 0) {
            if (e.key === 'ArrowDown') { e.preventDefault(); setMention((s) => ({ ...s, index: (s.index + 1) % s.items.length })); return; }
            if (e.key === 'ArrowUp') { e.preventDefault(); setMention((s) => ({ ...s, index: (s.index - 1 + s.items.length) % s.items.length })); return; }
            if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); selectMention(mention.items[mention.index]); return; }
            if (e.key === 'Escape') { e.preventDefault(); setMention({ open: false, items: [], index: 0 }); return; }
        }
        if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); }
    };

    const handleFileUpload = (e) => {
        const files = Array.from(e.target.files);
        const newAttachments = files.map(file => ({
            id: Date.now() + Math.random(),
            file,
            name: file.name,
            size: file.size,
            type: file.type
        }));
        setAttachments(prev => [...prev, ...newAttachments]);
    };

    const removeAttachment = (id) => {
        setAttachments(prev => prev.filter(att => att.id !== id));
    };

    // NOTE: the old "Skapa verifikat" modal + direct POST /verifications path is gone by
    // design. The agent platform stages create_verification behind HITL — the human
    // approves in the ToDo list, which is the ONLY commit path (no unapproved write
    // from the chat). The card in renderVerification is display-only.

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('sv-SE', { style: 'currency', currency: 'SEK' }).format(amount);
    };

    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 B';
        const k = 1024;
        const sizes = ['B', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
    };

    const copyToClipboard = (text, id) => {
        navigator.clipboard.writeText(text);
        setCopiedId(id);
        clearTimeout(copyTimerRef.current);
        copyTimerRef.current = setTimeout(() => setCopiedId(null), 2000);
    };
    useEffect(() => () => clearTimeout(copyTimerRef.current), []);

    const calculateVerificationTotals = (transactions) => {
        const debit = transactions.filter(t => t.amount > 0).reduce((sum, t) => sum + t.amount, 0);
        const credit = transactions.filter(t => t.amount < 0).reduce((sum, t) => sum + Math.abs(t.amount), 0);
        return { debit, credit, balanced: Math.abs(debit - credit) < 0.01 };
    };

    const handleExampleClick = (example) => {
        setInputMessage(example.message);
        textareaRef.current?.focus();
    };

    // ─── Render helpers ─────────────────────────────────────────

    const VISIBLE_SOURCES = 2;

    const SourceChip = ({ source }) => (
        source.url ? (
            <a href={source.url} target="_blank" rel="noopener noreferrer" className="finn-source-chip">
                <ExternalLink size={12} />
                {source.label}
            </a>
        ) : (
            <span className="finn-source-chip">
                <span className="finn-source-dot" />
                {source.label}
            </span>
        )
    );

    const ExpandableSources = ({ sources }) => {
        const [expanded, setExpanded] = useState(false);
        const visible = sources.slice(0, VISIBLE_SOURCES);
        const hidden = sources.slice(VISIBLE_SOURCES);
        const hasMore = hidden.length > 0;

        return (
            <div className="finn-sources">
                <div className="finn-sources-row">
                    <div className="finn-sources-label">Källor</div>
                    <div className="finn-sources-list">
                        {visible.map((s, i) => <SourceChip key={i} source={s} />)}
                        {hasMore && !expanded && (
                            <button
                                className="finn-source-more"
                                onClick={() => setExpanded(true)}
                                title={`Visa ${hidden.length} till`}
                            >
                                +{hidden.length}
                            </button>
                        )}
                    </div>
                </div>
                {hasMore && expanded && (
                    <div className="finn-sources-overflow">
                        <div className="finn-sources-list">
                            {hidden.map((s, i) => <SourceChip key={i + VISIBLE_SOURCES} source={s} />)}
                        </div>
                        <button className="finn-source-less" onClick={() => setExpanded(false)}>
                            Visa färre
                        </button>
                    </div>
                )}
            </div>
        );
    };

    const renderSources = (structuredSources, text) => {
        const sources = (structuredSources && structuredSources.length > 0)
            ? structuredSources
            : extractSources(text);
        if (sources.length === 0) return null;
        return <ExpandableSources sources={sources} />;
    };

    const renderVerification = (verification) => {
        if (!verification?.transactions?.length) return null;
        const totals = calculateVerificationTotals(verification.transactions);
        return (
            <div className="finn-verification">
                <div className="finn-verification-header">
                    <span>Verifikat</span>
                    <span className="finn-verification-meta">{verification.date} — {verification.description}</span>
                </div>
                <table className="finn-verification-table">
                    <thead>
                        <tr>
                            <th>Konto</th>
                            <th>Beskrivning</th>
                            <th style={{ textAlign: 'right' }}>Debet</th>
                            <th style={{ textAlign: 'right' }}>Kredit</th>
                        </tr>
                    </thead>
                    <tbody>
                        {verification.transactions.map((t, i) => (
                            <tr key={i}>
                                <td className="finn-account">{t.account}</td>
                                <td>{t.description}</td>
                                <td style={{ textAlign: 'right', color: t.amount > 0 ? '#b91c1c' : undefined, fontWeight: t.amount > 0 ? 600 : undefined }}>
                                    {t.amount > 0 ? formatCurrency(t.amount) : ''}
                                </td>
                                <td style={{ textAlign: 'right', color: t.amount < 0 ? '#15803d' : undefined, fontWeight: t.amount < 0 ? 600 : undefined }}>
                                    {t.amount < 0 ? formatCurrency(Math.abs(t.amount)) : ''}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                <div className="finn-verification-footer">
                    <span style={{ color: totals.balanced ? '#15803d' : '#b91c1c' }}>
                        {totals.balanced ? '✓ Balanserat' : '⚠ Ej balanserat'}
                    </span>
                    {/* No direct "Skapa verifikat" action: booking is staged by the agent
                        and committed via the ToDo approval (HITL) — the chat never holds
                        an unapproved write path. */}
                </div>
            </div>
        );
    };

    // ─── Main render ────────────────────────────────────────────

    return (
        <div className="finn-chat-page" ref={pageRef}>
            <style>{`
                /* ── Layout — flex column filling the content area, INNER scroll.
                   Width follows .content-body in normal flow, so expanding or
                   collapsing the sidebar just works (no fixed positioning). ── */
                .finn-chat-page {
                    background: #fafafa;
                    font-family: var(--font-family-base, 'Inter', sans-serif);
                    display: flex;
                    flex-direction: row; /* [thread history sidebar] + [chat column] */
                    overflow: hidden; /* the messages area owns the scroll */
                    position: relative; /* anchors the mobile overlay sidebar */
                }

                .finn-chat-main {
                    flex: 1;
                    min-width: 0;
                    display: flex;
                    flex-direction: column;
                    overflow: hidden;
                }

                .finn-header-actions {
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                }
                .finn-reset-btn.history-open {
                    background: var(--rgba-primary-1, rgba(68, 129, 78, 0.1));
                    color: var(--primary, #44814E);
                    border-color: var(--rgba-primary-3, rgba(68, 129, 78, 0.3));
                }

                .finn-header {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    padding: 0.75rem 1.5rem;
                    border-bottom: 1px solid #e5e7eb;
                    background: white;
                }

                .finn-header-left {
                    display: flex;
                    align-items: center;
                    gap: 0.75rem;
                }

                .finn-avatar {
                    width: 32px;
                    height: 32px;
                    border-radius: 10px;
                    background: var(--primary, #44814E);
                    color: white;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-weight: 700;
                    font-size: 0.875rem;
                    flex-shrink: 0;
                }

                .finn-header-info h1 {
                    font-size: 0.9375rem;
                    font-weight: 600;
                    margin: 0;
                    color: #111827;
                }

                .finn-header-status {
                    font-size: 0.75rem;
                    color: #6b7280;
                }

                .finn-header-status .dot {
                    display: inline-block;
                    width: 6px;
                    height: 6px;
                    border-radius: 50%;
                    margin-right: 4px;
                    vertical-align: middle;
                }

                .finn-header-status .dot.online { background: #22c55e; }
                .finn-header-status .dot.offline { background: #ef4444; }

                .finn-reset-btn {
                    background: none;
                    border: 1px solid #e5e7eb;
                    border-radius: 8px;
                    padding: 0.4rem 0.75rem;
                    font-size: 0.8125rem;
                    color: #6b7280;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    gap: 0.375rem;
                    transition: all 0.15s;
                }
                .finn-reset-btn:hover {
                    background: #f3f4f6;
                    color: #374151;
                }

                .finn-header {
                    flex-shrink: 0;
                }

                /* ── Messages area (the scroll container) ───── */
                .finn-messages-area {
                    flex: 1;
                    min-height: 0;
                    overflow-y: auto;
                    padding: 1.5rem 1rem;
                }

                .finn-messages-column {
                    max-width: 720px;
                    margin: 0 auto;
                    width: 100%;
                }

                /* ── Welcome ────────────────────────────────── */
                .finn-welcome {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    min-height: 60vh;
                    text-align: center;
                    padding: 2rem 1rem;
                }

                .finn-welcome-icon {
                    width: 56px;
                    height: 56px;
                    border-radius: 16px;
                    background: var(--primary, #44814E);
                    color: white;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 1.5rem;
                    font-weight: 700;
                    margin-bottom: 1.25rem;
                }

                .finn-welcome h2 {
                    font-size: 1.5rem;
                    font-weight: 700;
                    color: #111827;
                    margin-bottom: 0.5rem;
                }

                .finn-welcome p {
                    color: #6b7280;
                    font-size: 0.9375rem;
                    line-height: 1.6;
                    max-width: 480px;
                    margin-bottom: 2rem;
                }

                /* Specialist avatar row — same assets/fallbacks as the chat
                   bubbles (BotAvatar). Click = insert the @mention. */
                .finn-welcome-team {
                    display: flex;
                    flex-wrap: wrap;
                    justify-content: center;
                    gap: 0.25rem 0.5rem;
                    margin: -0.5rem 0 2rem;
                    max-width: 560px;
                }
                .finn-team-avatar {
                    background: none;
                    border: none;
                    cursor: pointer;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    gap: 0.3rem;
                    padding: 0.375rem 0.5rem;
                    border-radius: 12px;
                    transition: all 0.15s;
                }
                .finn-team-avatar:hover {
                    background: var(--rgba-primary-1, rgba(68, 129, 78, 0.1));
                    transform: translateY(-2px);
                }
                .finn-team-avatar-img {
                    width: 40px;
                    height: 40px;
                    border-radius: 12px;
                    overflow: hidden;
                    display: block;
                }
                .finn-team-avatar-name {
                    font-size: 0.6875rem;
                    font-weight: 500;
                    color: #6b7280;
                }
                .finn-team-avatar:hover .finn-team-avatar-name {
                    color: var(--primary, #44814E);
                }

                .finn-examples {
                    display: flex;
                    flex-wrap: wrap;
                    gap: 0.5rem;
                    justify-content: center;
                    max-width: 560px;
                }

                .finn-example-btn {
                    background: white;
                    border: 1px solid #e5e7eb;
                    border-radius: 12px;
                    padding: 0.625rem 1rem;
                    font-size: 0.8125rem;
                    color: #374151;
                    cursor: pointer;
                    transition: all 0.15s;
                    text-align: left;
                    line-height: 1.4;
                }
                .finn-example-btn:hover {
                    border-color: var(--primary, #44814E);
                    background: var(--rgba-primary-1, rgba(68, 129, 78, 0.1));
                    color: var(--primary, #44814E);
                }

                /* ── Message rows ───────────────────────────── */
                .finn-msg {
                    margin-bottom: 1.5rem;
                    animation: finnFadeIn 0.2s ease-out;
                }

                @keyframes finnFadeIn {
                    from { opacity: 0; transform: translateY(6px); }
                    to { opacity: 1; transform: translateY(0); }
                }

                /* User message */
                .finn-msg-user {
                    display: flex;
                    justify-content: flex-end;
                }

                .finn-msg-user-bubble {
                    max-width: 80%;
                    background: var(--rgba-primary-2, rgba(68, 129, 78, 0.15));
                    color: #1a2e1d;
                    padding: 0.75rem 1rem;
                    border-radius: 16px 16px 4px 16px;
                    font-size: 0.9375rem;
                    line-height: 1.55;
                    white-space: pre-wrap;
                }

                .finn-msg-user-time {
                    text-align: right;
                    font-size: 0.6875rem;
                    color: #9ca3af;
                    margin-top: 0.25rem;
                    padding-right: 0.25rem;
                }

                /* @mention in sent messages — bolder light-blue text (no pill) + hover bubble */
                .finn-mention-chip {
                    color: #3b82f6;
                    font-weight: 600;
                    position: relative;
                    cursor: default;
                    white-space: nowrap;
                }
                .finn-mention-tip {
                    display: none;
                    position: absolute;
                    bottom: calc(100% + 6px);
                    left: 0;
                    width: 230px;
                    padding: 9px 11px;
                    background: #fff;
                    color: #374151;
                    border: 1px solid #e5e7eb;
                    border-radius: 9px;
                    box-shadow: 0 10px 28px rgba(0, 0, 0, 0.14);
                    font-weight: 400;
                    font-size: 0.76rem;
                    line-height: 1.4;
                    z-index: 40;
                    white-space: normal;
                    text-align: left;
                }
                .finn-mention-chip:hover .finn-mention-tip { display: block; }
                .finn-mention-tip-name { display: block; font-weight: 700; color: #111827; margin-bottom: 3px; }
                .finn-mention-tip-blurb { display: block; color: #6b7280; }

                /* Bot message */
                .finn-msg-bot {
                    display: flex;
                    gap: 0.75rem;
                    align-items: flex-start;
                }

                .finn-msg-bot-avatar {
                    width: 28px;
                    height: 28px;
                    border-radius: 8px;
                    background: var(--primary, #44814E);
                    color: white;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-weight: 700;
                    font-size: 0.75rem;
                    flex-shrink: 0;
                    margin-top: 2px;
                }

                .finn-msg-bot-content {
                    flex: 1;
                    min-width: 0;
                }

                .finn-msg-bot-name {
                    font-size: 0.8125rem;
                    font-weight: 600;
                    color: #374151;
                    margin-bottom: 0.25rem;
                }

                .finn-msg-bot-text {
                    font-size: 0.9375rem;
                    line-height: 1.65;
                    color: #1f2937;
                }

                /* Markdown styling within bot messages */
                .finn-msg-bot-text p {
                    margin: 0 0 0.75rem;
                }
                .finn-msg-bot-text p:last-child {
                    margin-bottom: 0;
                }
                .finn-msg-bot-text ul,
                .finn-msg-bot-text ol {
                    padding-left: 1.25rem;
                    margin: 0.5rem 0;
                }
                .finn-msg-bot-text li {
                    margin-bottom: 0.25rem;
                }
                .finn-msg-bot-text strong {
                    font-weight: 600;
                    color: #111827;
                }
                .finn-msg-bot-text code {
                    background: #f3f4f6;
                    padding: 0.125rem 0.375rem;
                    border-radius: 4px;
                    font-size: 0.85em;
                }
                .finn-msg-bot-text pre {
                    background: #f9fafb;
                    border: 1px solid #e5e7eb;
                    border-radius: 8px;
                    padding: 0.75rem 1rem;
                    overflow-x: auto;
                    margin: 0.75rem 0;
                }
                .finn-msg-bot-text pre code {
                    background: none;
                    padding: 0;
                }
                .finn-msg-bot-text a {
                    color: var(--primary, #44814E);
                    text-decoration: underline;
                    text-underline-offset: 2px;
                }
                .finn-msg-bot-text table {
                    width: 100%;
                    border-collapse: collapse;
                    margin: 0.75rem 0;
                    font-size: 0.875rem;
                }
                .finn-msg-bot-text th,
                .finn-msg-bot-text td {
                    border: 1px solid #e5e7eb;
                    padding: 0.5rem 0.75rem;
                    text-align: left;
                }
                .finn-msg-bot-text th {
                    background: #f9fafb;
                    font-weight: 600;
                }
                .finn-msg-bot-text h1, .finn-msg-bot-text h2, .finn-msg-bot-text h3 {
                    font-size: 1rem;
                    font-weight: 600;
                    margin: 1rem 0 0.5rem;
                    color: #111827;
                }

                /* Bot actions (show on hover) */
                .finn-msg-bot-actions {
                    display: flex;
                    gap: 0.25rem;
                    margin-top: 0.5rem;
                    opacity: 0;
                    transition: opacity 0.15s;
                }
                .finn-msg-bot-content:hover .finn-msg-bot-actions {
                    opacity: 1;
                }
                .finn-action-btn {
                    background: none;
                    border: none;
                    color: #9ca3af;
                    cursor: pointer;
                    padding: 0.25rem;
                    border-radius: 4px;
                    display: flex;
                    align-items: center;
                    gap: 0.25rem;
                    font-size: 0.75rem;
                    transition: all 0.15s;
                }
                .finn-action-btn:hover {
                    color: #6b7280;
                    background: #f3f4f6;
                }
                .finn-msg-bot-time {
                    font-size: 0.6875rem;
                    color: #9ca3af;
                    margin-left: auto;
                }

                /* System message */
                .finn-msg-system {
                    text-align: center;
                    margin: 1rem 0;
                }
                .finn-msg-system-text {
                    display: inline-block;
                    font-size: 0.8125rem;
                    color: #6b7280;
                    background: #f3f4f6;
                    padding: 0.375rem 0.875rem;
                    border-radius: 10px;
                }

                /* Error message */
                .finn-msg-error .finn-msg-bot-text {
                    color: #b91c1c;
                    background: #fef2f2;
                    border: 1px solid #fecaca;
                    border-radius: 8px;
                    padding: 0.75rem 1rem;
                }

                /* ── Sources ────────────────────────────────── */
                .finn-sources {
                    margin-top: 0.75rem;
                    padding-top: 0.75rem;
                    border-top: 1px solid #f3f4f6;
                }
                .finn-sources-row {
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                }
                .finn-sources-label {
                    font-size: 0.6875rem;
                    font-weight: 600;
                    color: #9ca3af;
                    text-transform: uppercase;
                    letter-spacing: 0.05em;
                    white-space: nowrap;
                }
                .finn-sources-list {
                    display: flex;
                    flex-wrap: wrap;
                    gap: 0.375rem;
                    align-items: center;
                }
                .finn-source-chip {
                    display: inline-flex;
                    align-items: center;
                    gap: 0.25rem;
                    font-size: 0.6875rem;
                    padding: 0.1875rem 0.5rem;
                    border-radius: 999px;
                    background: #f8fafc;
                    color: #64748b;
                    text-decoration: none;
                    border: 1px solid #e2e8f0;
                    transition: all 0.2s ease;
                    max-width: 200px;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                }
                a.finn-source-chip:hover {
                    background: #f1f5f9;
                    border-color: #94a3b8;
                    color: #334155;
                }
                .finn-source-dot {
                    width: 5px;
                    height: 5px;
                    border-radius: 50%;
                    background: #cbd5e1;
                    flex-shrink: 0;
                }
                .finn-source-more {
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 0.625rem;
                    font-weight: 700;
                    min-width: 28px;
                    height: 22px;
                    padding: 0 0.375rem;
                    border-radius: 999px;
                    background: #e2e8f0;
                    color: #475569;
                    border: none;
                    cursor: pointer;
                    transition: all 0.2s ease;
                    letter-spacing: -0.02em;
                }
                .finn-source-more:hover {
                    background: #cbd5e1;
                    color: #1e293b;
                    transform: scale(1.05);
                }
                .finn-sources-overflow {
                    display: flex;
                    flex-wrap: wrap;
                    align-items: center;
                    gap: 0.375rem;
                    margin-top: 0.375rem;
                    padding-left: calc(0.5rem + 3.5em);
                    animation: finnSourcesExpand 0.25s ease-out;
                }
                @keyframes finnSourcesExpand {
                    from { opacity: 0; transform: translateY(-4px); max-height: 0; }
                    to { opacity: 1; transform: translateY(0); max-height: 200px; }
                }
                .finn-source-less {
                    font-size: 0.625rem;
                    color: #94a3b8;
                    background: none;
                    border: none;
                    cursor: pointer;
                    padding: 0.125rem 0.25rem;
                    border-radius: 4px;
                    transition: color 0.15s;
                }
                .finn-source-less:hover {
                    color: #475569;
                }

                /* ── Verification card ──────────────────────── */
                .finn-verification {
                    background: white;
                    border: 1px solid #e5e7eb;
                    border-radius: 10px;
                    margin-top: 0.75rem;
                    overflow: hidden;
                }
                .finn-verification-header {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    padding: 0.625rem 0.875rem;
                    background: #f9fafb;
                    border-bottom: 1px solid #e5e7eb;
                    font-weight: 600;
                    font-size: 0.8125rem;
                    color: #374151;
                }
                .finn-verification-meta {
                    font-weight: 400;
                    color: #6b7280;
                    font-size: 0.75rem;
                }
                .finn-verification-table {
                    width: 100%;
                    border-collapse: collapse;
                    font-size: 0.8125rem;
                }
                .finn-verification-table th {
                    padding: 0.5rem 0.875rem;
                    background: #f9fafb;
                    font-weight: 600;
                    color: #374151;
                    border-bottom: 1px solid #e5e7eb;
                    text-align: left;
                }
                .finn-verification-table td {
                    padding: 0.5rem 0.875rem;
                    border-bottom: 1px solid #f3f4f6;
                    color: #374151;
                }
                .finn-account {
                    font-family: 'SF Mono', 'Fira Code', monospace;
                    font-weight: 600;
                    color: #111827;
                }
                .finn-verification-footer {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    padding: 0.625rem 0.875rem;
                    background: #f9fafb;
                    border-top: 1px solid #e5e7eb;
                    font-size: 0.8125rem;
                    font-weight: 600;
                }
                .finn-verification-actions {
                    display: flex;
                    gap: 0.375rem;
                }

                /* ── Artifacts (financial tables, KPIs, charts, invoice lists) ─── */
                .finn-artifacts {
                    display: flex;
                    flex-direction: column;
                    gap: 0.75rem;
                    margin-top: 0.75rem;
                }
                .finn-artifact {
                    background: white;
                    border: 1px solid #e5e7eb;
                    border-radius: 10px;
                    overflow: hidden;
                    animation: finnFadeIn 0.3s ease-out;
                }
                .finn-artifact-header {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    padding: 0.5rem 0.875rem;
                    background: #f8fafc;
                    border-bottom: 1px solid #e5e7eb;
                }
                .finn-artifact-title {
                    font-weight: 600;
                    font-size: 0.8125rem;
                    color: #1e293b;
                }
                .finn-artifact-period {
                    font-size: 0.6875rem;
                    color: #94a3b8;
                    font-weight: 500;
                }
                .finn-artifact-total {
                    font-size: 0.8125rem;
                    font-weight: 600;
                    color: #1e293b;
                    font-variant-numeric: tabular-nums;
                }

                /* Financial Table */
                .finn-financial-table table,
                .finn-invoice-list table {
                    width: 100%;
                    border-collapse: collapse;
                    font-size: 0.8125rem;
                }
                .finn-financial-table th,
                .finn-invoice-list th {
                    padding: 0.4375rem 0.875rem;
                    background: #f8fafc;
                    font-weight: 600;
                    font-size: 0.75rem;
                    color: #64748b;
                    text-transform: uppercase;
                    letter-spacing: 0.03em;
                    border-bottom: 1px solid #e5e7eb;
                }
                .finn-financial-table td,
                .finn-invoice-list td {
                    padding: 0.4375rem 0.875rem;
                    border-bottom: 1px solid #f1f5f9;
                    color: #334155;
                }
                .finn-financial-table tbody tr:hover,
                .finn-invoice-list tbody tr:hover {
                    background: #f8fafc;
                }
                .finn-num {
                    font-variant-numeric: tabular-nums;
                    font-feature-settings: 'tnum';
                }
                .finn-num-mono {
                    font-family: 'SF Mono', 'Fira Code', monospace;
                    font-weight: 600;
                    color: #1e293b;
                    font-size: 0.75rem;
                }
                .finn-negative {
                    color: #b91c1c;
                }
                .finn-totals-row td {
                    font-weight: 700;
                    color: #0f172a;
                    border-top: 2px solid #cbd5e1;
                    border-bottom: none;
                    background: #f8fafc;
                }
                .finn-table-scroll {
                    overflow-x: auto;
                }

                /* KPI Grid */
                .finn-kpi-cards {
                    display: grid;
                    gap: 0.5rem;
                    padding: 0.75rem;
                }
                .finn-kpi-card {
                    background: #f8fafc;
                    border: 1px solid #e2e8f0;
                    border-radius: 8px;
                    padding: 0.75rem;
                    text-align: center;
                }
                .finn-kpi-label {
                    font-size: 0.6875rem;
                    font-weight: 600;
                    color: #64748b;
                    text-transform: uppercase;
                    letter-spacing: 0.05em;
                    margin-bottom: 0.25rem;
                }
                .finn-kpi-value {
                    font-size: 1.375rem;
                    font-weight: 700;
                    color: #0f172a;
                    font-variant-numeric: tabular-nums;
                    line-height: 1.2;
                }
                .finn-kpi-trend {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 0.25rem;
                    margin-top: 0.375rem;
                    font-size: 0.75rem;
                    font-weight: 600;
                }
                .finn-kpi-trend-up { color: #15803d; }
                .finn-kpi-trend-down { color: #b91c1c; }
                .finn-kpi-trend-flat { color: #64748b; }
                .finn-kpi-trend-label {
                    font-weight: 400;
                    color: #94a3b8;
                }

                /* Invoice badges */
                .finn-badge {
                    display: inline-flex;
                    align-items: center;
                    gap: 0.125rem;
                    font-size: 0.6875rem;
                    font-weight: 600;
                    padding: 0.125rem 0.5rem;
                    border-radius: 4px;
                }
                .finn-badge-paid {
                    background: #dcfce7;
                    color: #15803d;
                }
                .finn-badge-unpaid {
                    background: #fef3c7;
                    color: #92400e;
                }
                .finn-badge-overdue {
                    background: #fee2e2;
                    color: #b91c1c;
                }
                .finn-badge-pending {
                    background: #e0e7ff;
                    color: #3730a3;
                }
                .finn-badge-days {
                    font-weight: 400;
                }
                .finn-row-overdue {
                    background: #fef2f2 !important;
                }

                /* Bar Chart */
                .finn-chart-body {
                    padding: 0.75rem;
                    display: flex;
                    gap: 0.375rem;
                    align-items: flex-end;
                }
                .finn-chart-ylabel {
                    writing-mode: vertical-rl;
                    transform: rotate(180deg);
                    font-size: 0.625rem;
                    color: #94a3b8;
                    text-align: center;
                    padding-bottom: 1.25rem;
                }
                .finn-chart-bars {
                    flex: 1;
                    display: flex;
                    align-items: flex-end;
                    gap: 0.25rem;
                    height: 140px;
                }
                .finn-chart-column {
                    flex: 1;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    min-width: 0;
                }
                .finn-chart-bar-group {
                    display: flex;
                    gap: 2px;
                    align-items: flex-end;
                    width: 100%;
                    height: 120px;
                }
                .finn-chart-bar {
                    flex: 1;
                    border-radius: 3px 3px 0 0;
                    transition: height 0.4s ease-out;
                    min-height: 2px;
                    cursor: default;
                }
                .finn-chart-bar-primary {
                    background: #3b82f6;
                }
                .finn-chart-bar-secondary {
                    background: #f59e0b;
                }
                .finn-chart-bar:hover {
                    opacity: 0.8;
                }
                .finn-chart-label {
                    font-size: 0.625rem;
                    color: #64748b;
                    margin-top: 0.25rem;
                    text-align: center;
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    max-width: 100%;
                }
                .finn-chart-legend {
                    display: flex;
                    gap: 1rem;
                    justify-content: center;
                    padding: 0.5rem;
                    font-size: 0.6875rem;
                    color: #64748b;
                }
                .finn-chart-legend-item {
                    display: flex;
                    align-items: center;
                    gap: 0.25rem;
                }
                .finn-chart-dot {
                    width: 8px;
                    height: 8px;
                    border-radius: 2px;
                }
                .finn-chart-dot-primary { background: #3b82f6; }
                .finn-chart-dot-secondary { background: #f59e0b; }

                /* ── Typing indicator ───────────────────────── */
                .finn-typing {
                    display: flex;
                    gap: 0.75rem;
                    align-items: flex-start;
                    margin-bottom: 1.5rem;
                }
                .finn-typing-dots {
                    display: flex;
                    gap: 4px;
                    padding-top: 0.5rem;
                }
                .finn-typing-dot {
                    width: 6px;
                    height: 6px;
                    border-radius: 50%;
                    background: #9ca3af;
                    animation: finnBounce 1.4s infinite ease-in-out;
                }
                .finn-typing-dot:nth-child(1) { animation-delay: -0.32s; }
                .finn-typing-dot:nth-child(2) { animation-delay: -0.16s; }
                .finn-typing-dot:nth-child(3) { animation-delay: 0s; }

                @keyframes finnBounce {
                    0%, 80%, 100% { transform: scale(0.7); opacity: 0.4; }
                    40% { transform: scale(1); opacity: 1; }
                }

                .finn-memory-proposal {
                    width: 100%;
                    max-width: 34rem;
                    border: 1px solid #e5e7eb;
                    border-radius: 0.75rem;
                    background: #fafafa;
                    padding: 0.75rem 0.875rem;
                    text-align: left;
                }
                .finn-memory-proposal-row {
                    display: flex;
                    flex-wrap: wrap;
                    align-items: center;
                    justify-content: space-between;
                    gap: 0.5rem;
                }
                .finn-memory-proposal-text {
                    font-size: 0.8125rem;
                    color: #6b7280;
                    flex: 1 1 16rem;
                }
                /* The exact sentence a confirm stores — quoted, so it reads as a claim to approve
                   rather than as the assistant's own prose. */
                .finn-memory-proposal-claim {
                    color: #111827;
                    font-size: 0.875rem;
                    margin-top: 0.125rem;
                }
                .finn-memory-proposal-scope {
                    font-size: 0.75rem;
                    color: #9ca3af;
                    margin-top: 0.125rem;
                }
                .finn-memory-proposal-actions {
                    display: flex;
                    align-items: center;
                    gap: 0.25rem;
                    flex-shrink: 0;
                }
                .finn-memory-proposal-done,
                .finn-memory-proposal-error {
                    font-size: 0.8125rem;
                    color: #6b7280;
                }
                .finn-memory-proposal-error { color: #b91c1c; }

                .finn-activity {
                    display: flex;
                    flex-direction: column;
                    gap: 0.25rem;
                    padding-top: 0.375rem;
                    font-size: 0.8125rem;
                    color: #9ca3af;
                }
                .finn-activity-step {
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    /* Steps arrive one at a time; fading them in stops the block from jumping. */
                    animation: finnActivityIn 0.18s ease-out;
                }
                .finn-activity-step.is-current {
                    color: #4b5563;
                }
                .finn-activity-bullet {
                    width: 5px;
                    height: 5px;
                    border-radius: 50%;
                    background: currentColor;
                    opacity: 0.5;
                    flex-shrink: 0;
                }
                .finn-activity-step.is-current .finn-activity-bullet {
                    opacity: 1;
                    animation: finnBounce 1.4s infinite ease-in-out;
                }
                @keyframes finnActivityIn {
                    from { opacity: 0; transform: translateY(-2px); }
                    to { opacity: 1; transform: none; }
                }

                .finn-typing-label {
                    font-size: 0.8125rem;
                    color: #9ca3af;
                    padding-top: 0.375rem;
                }

                /* ── Input area — normal flow at the bottom of the flex column ── */
                .finn-input-area {
                    flex-shrink: 0;
                    background: #fafafa;
                    border-top: 1px solid #e5e7eb;
                    padding: 0.75rem 1rem 1rem;
                }

                /* ── Proactive alerts + staged-approval chip ── */
                .finn-alerts {
                    display: flex;
                    flex-direction: column;
                    gap: 0.375rem;
                    margin-bottom: 0.625rem;
                }
                .finn-alert {
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    font-size: 0.8125rem;
                    border-radius: 10px;
                    padding: 0.5rem 0.75rem;
                    border: 1px solid #e5e7eb;
                    background: #f8fafc;
                    color: #334155;
                }
                .finn-alert-critical { background: #fef2f2; border-color: #fecaca; color: #991b1b; }
                .finn-alert-warning  { background: #fffbeb; border-color: #fde68a; color: #92400e; }
                .finn-alert-dot {
                    width: 7px; height: 7px; border-radius: 50%; flex-shrink: 0;
                    background: #64748b;
                }
                .finn-alert-critical .finn-alert-dot { background: #dc2626; }
                .finn-alert-warning  .finn-alert-dot { background: #d97706; }

                .finn-approval-chip {
                    display: inline-flex;
                    align-items: center;
                    gap: 0.5rem;
                    margin-top: 0.5rem;
                    font-size: 0.8125rem;
                    font-weight: 500;
                    color: #92400e;
                    background: #fffbeb;
                    border: 1px solid #fde68a;
                    border-radius: 10px;
                    padding: 0.5rem 0.75rem;
                    text-decoration: none;
                }
                .finn-approval-chip:hover { background: #fef3c7; color: #78350f; }

                .finn-input-column {
                    max-width: 720px;
                    margin: 0 auto;
                }

                .finn-attachments {
                    display: flex;
                    flex-wrap: wrap;
                    gap: 0.375rem;
                    margin-bottom: 0.5rem;
                }
                .finn-attachment-chip {
                    display: flex;
                    align-items: center;
                    gap: 0.375rem;
                    background: #f3f4f6;
                    border: 1px solid #e5e7eb;
                    border-radius: 8px;
                    padding: 0.3rem 0.625rem;
                    font-size: 0.75rem;
                    color: #4b5563;
                }
                .finn-attachment-remove {
                    background: none;
                    border: none;
                    color: #9ca3af;
                    cursor: pointer;
                    padding: 0;
                    font-size: 1rem;
                    line-height: 1;
                    margin-left: 0.25rem;
                }
                .finn-attachment-remove:hover { color: #ef4444; }

                .finn-input-row {
                    display: flex;
                    align-items: flex-end;
                    gap: 0.5rem;
                }

                .finn-input-box {
                    flex: 1;
                    background: #f9fafb;
                    border: 1.5px solid #e5e7eb;
                    border-radius: 14px;
                    padding: 0;
                    display: flex;
                    align-items: flex-end;
                    transition: border-color 0.15s;
                    overflow: hidden;
                }
                .finn-input-box:focus-within {
                    border-color: var(--primary, #44814E);
                    box-shadow: 0 0 0 3px var(--rgba-primary-1, rgba(68, 129, 78, 0.1));
                }

                /* The textarea sits over a highlight backdrop: its own text is transparent (the
                   backdrop renders the visible, @mention-highlighted copy), caret stays visible.
                   Metrics here MUST match .finn-input-highlight exactly or the caret drifts. */
                .finn-textarea-wrap {
                    flex: 1;
                    position: relative;
                    display: flex;
                    min-width: 0;
                }
                .finn-textarea {
                    flex: 1;
                    width: 100%;
                    border: none;
                    outline: none;
                    resize: none;
                    padding: 0.875rem 1rem;
                    font-family: inherit;
                    font-size: 0.9375rem;
                    line-height: 1.5;
                    background: transparent;
                    color: transparent;
                    caret-color: #1f2937;
                    position: relative;
                    z-index: 1;
                    min-height: 64px;
                    max-height: 150px;
                }
                .finn-textarea::placeholder { color: #9ca3af; }
                .finn-input-highlight {
                    position: absolute;
                    inset: 0;
                    padding: 0.875rem 1rem;
                    font-family: inherit;
                    font-size: 0.9375rem;
                    line-height: 1.5;
                    color: #1f2937;
                    white-space: pre-wrap;
                    overflow-wrap: break-word;
                    word-break: break-word;
                    overflow: hidden;
                    pointer-events: none;
                    z-index: 0;
                }
                .finn-mention-inline {
                    color: #3b82f6;
                    text-shadow: 0 0 0.45px currentColor; /* faux-bold: looks bolder, same width */
                }

                .finn-input-controls {
                    display: flex;
                    align-items: center;
                    gap: 0.125rem;
                    padding: 0.375rem 0.5rem;
                }

                .finn-input-icon-btn {
                    background: none;
                    border: none;
                    color: #9ca3af;
                    cursor: pointer;
                    padding: 0.375rem;
                    border-radius: 6px;
                    display: flex;
                    align-items: center;
                    transition: all 0.15s;
                }
                .finn-input-icon-btn:hover:not(:disabled) {
                    color: #6b7280;
                    background: #e5e7eb;
                }
                .finn-input-icon-btn:disabled { opacity: 0.4; cursor: not-allowed; }

                .finn-send-btn {
                    width: 44px;
                    height: 44px;
                    border-radius: 12px;
                    border: none;
                    background: var(--primary, #44814E);
                    color: white;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    transition: all 0.15s;
                    flex-shrink: 0;
                }
                .finn-send-btn:hover:not(:disabled) {
                    background: var(--primary-hover, #32603a);
                }
                .finn-send-btn:disabled {
                    background: #d1d5db;
                    cursor: not-allowed;
                }

                .finn-disclaimer {
                    text-align: center;
                    margin-top: 0.5rem;
                    font-size: 0.6875rem;
                    color: #9ca3af;
                }
            `}</style>

            {/* Thread history sidebar (ChatGPT-style) */}
            {historyOpen && (
                <ThreadHistoryPanel
                    threads={threads}
                    onNewThread={startNewThread}
                    onSelectThread={resumeThread}
                    onRenameThread={renameThread}
                    onArchiveThread={archiveThread}
                />
            )}

            <div className="finn-chat-main">

            {/* Header */}
            <div className="finn-header">
                <div className="finn-header-left">
                    <div className="finn-avatar"><img src={FINN_AVATAR} alt="Finn" style={finnAvatarImg} /></div>
                    <div className="finn-header-info">
                        <h1>Finn</h1>
                        <div className="finn-header-status">
                            <span className={`dot ${isConnected ? 'online' : 'offline'}`}></span>
                            {isConnected ? 'Online' : 'Ansluter...'}
                        </div>
                    </div>
                </div>
                <div className="finn-header-actions">
                    <button
                        className={`finn-reset-btn ${historyOpen ? 'history-open' : ''}`}
                        onClick={toggleHistory}
                        title={historyOpen ? 'Dölj tidigare konversationer' : 'Visa tidigare konversationer'}
                    >
                        <History size={14} />
                        Historik
                    </button>
                    <button className="finn-reset-btn" onClick={startNewThread}>
                        <RotateCcw size={14} />
                        Ny chatt
                    </button>
                </div>
            </div>

            {/* Messages */}
            <div className="finn-messages-area">
                <div className="finn-messages-column">
                    {/* Welcome screen */}
                    {messages.length === 0 && (
                        <div className="finn-welcome">
                            <div className="finn-welcome-icon"><img src={FINN_AVATAR} alt="Finn" style={finnAvatarImg} /></div>
                            <h2>Hej, jag är Finn!</h2>
                            <p>
                                Din AI-ekonomichef. Jag leder ett team av specialister och hjälper
                                dig med hela företagets ekonomi – bokföring, fakturor, lön, moms
                                och rapporter. Beskriv vad du behöver med egna ord, ladda upp ett
                                underlag, eller skriv @ för att nå en specialist direkt.
                            </p>
                            {/* The team, minus Finn (he's the welcome icon above). Hidden
                                until the roster loads; older Finn-only gateways never load
                                one, so they never show a team they can't route to. */}
                            {agentRoster.length > 1 && (
                                <div className="finn-welcome-team" aria-label="Specialistteamet">
                                    {agentRoster
                                        .filter((a) => !['@finn', '@cfo'].includes(String(a.handle).toLowerCase()))
                                        .slice(0, 8)
                                        .map((a) => (
                                            <button
                                                key={a.handle}
                                                className="finn-team-avatar"
                                                title={`${a.displayName}${a.role ? ` – ${a.role}` : ''}`}
                                                onClick={() => {
                                                    setInputMessage((prev) => (
                                                        prev.trimStart().toLowerCase().startsWith(String(a.handle).toLowerCase())
                                                            ? prev
                                                            : `${a.handle} ${prev}`
                                                    ));
                                                    setTimeout(() => textareaRef.current?.focus(), 0);
                                                }}
                                            >
                                                <span className="finn-team-avatar-img"><BotAvatar agent={a} /></span>
                                                <span className="finn-team-avatar-name">{a.displayName}</span>
                                            </button>
                                        ))}
                                </div>
                            )}
                            <div className="finn-examples">
                                {quickStartExamples.map((ex, i) => (
                                    <button key={i} className="finn-example-btn" onClick={() => handleExampleClick(ex)}>
                                        {ex.message}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Messages */}
                    {messages.map((msg) => {
                        if (msg.sender === 'system') {
                            if (msg.kind === 'memory-proposal') {
                                return (
                                    <div key={msg.id} className="finn-msg finn-msg-system">
                                        <MemoryProposalCard
                                            proposals={msg.proposals}
                                            companyId={companyId}
                                            isAdmin={isMemoryAdmin}
                                        />
                                    </div>
                                );
                            }
                            return (
                                <div key={msg.id} className="finn-msg finn-msg-system">
                                    <span className="finn-msg-system-text">{msg.message}</span>
                                </div>
                            );
                        }

                        if (msg.sender === 'user') {
                            return (
                                <div key={msg.id} className="finn-msg">
                                    <div className="finn-msg-user">
                                        <div className="finn-msg-user-bubble">{renderWithMentions(msg.message, agentRoster)}</div>
                                    </div>
                                    {msg.timestamp && (
                                        <div className="finn-msg-user-time">
                                            {msg.timestamp.toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' })}
                                        </div>
                                    )}
                                </div>
                            );
                        }

                        // Bot message — show who actually answered (P3): the active agent's
                        // avatar + name; falls back to Finn when unknown.
                        const displayText = stripSourcesSection(msg.message || '');
                        const botName = msg.agent?.displayName || 'Finn';
                        // Proactive alerts are Finn's CFO overview — don't attribute them to a
                        // specialist (a payroll reply shouldn't surface invoice/bank alerts).
                        const isFinn = !msg.agent || msg.agent.name === 'cfo';
                        return (
                            <div key={msg.id} className={`finn-msg finn-msg-bot ${msg.error ? 'finn-msg-error' : ''}`}>
                                <div className="finn-msg-bot-avatar"><BotAvatar agent={msg.agent} /></div>
                                <div className="finn-msg-bot-content">
                                    <div className="finn-msg-bot-name">{botName}</div>

                                    {/* Proactive alerts (first message of a session) — Finn only */}
                                    {isFinn && msg.alerts?.length > 0 && (
                                        <div className="finn-alerts">
                                            {msg.alerts.map((alert, i) => (
                                                <div key={i} className={`finn-alert finn-alert-${alert.severity}`}>
                                                    <span className="finn-alert-dot" />
                                                    {alert.message}
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    <div className="finn-msg-bot-text">
                                        <ReactMarkdown remarkPlugins={[remarkGfm]}>{displayText}</ReactMarkdown>
                                    </div>

                                    {/* Structured artifacts (tables, KPIs, charts) */}
                                    {msg.artifacts?.length > 0 && (
                                        <div className="finn-artifacts">
                                            {msg.artifacts.map((artifact, i) => (
                                                <ArtifactRenderer key={artifact.id || i} artifact={artifact} />
                                            ))}
                                        </div>
                                    )}

                                    {/* Staged HITL approval → review in the ToDo list */}
                                    {msg.requiresApproval && (
                                        <Link to={`/${companyId}/todos`} className="finn-approval-chip">
                                            <ShieldCheck size={14} />
                                            Väntar på ditt godkännande — granska i Att göra
                                            <ChevronRight size={14} />
                                        </Link>
                                    )}

                                    {/* Verification card (display only — booking goes via HITL/ToDo) */}
                                    {msg.verification && renderVerification(msg.verification)}

                                    {/* Source references */}
                                    {!msg.error && renderSources(msg.sources, msg.message || '')}

                                    {/* Actions (hover) */}
                                    {!msg.error && (
                                        <div className="finn-msg-bot-actions">
                                            <button className="finn-action-btn" onClick={() => copyToClipboard(msg.message, msg.id)}>
                                                {copiedId === msg.id ? <Check size={12} /> : <Copy size={12} />}
                                                {copiedId === msg.id ? 'Kopierat' : ''}
                                            </button>
                                            <button className="finn-action-btn"><ThumbsUp size={12} /></button>
                                            <button className="finn-action-btn"><ThumbsDown size={12} /></button>
                                            {msg.timestamp && (
                                                <span className="finn-msg-bot-time">
                                                    {msg.timestamp.toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' })}
                                                </span>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}

                    {/* Typing + the live activity trail */}
                    {isTyping && (
                        <div className="finn-typing">
                            {/* Neutral working indicator — no agent attributed, since we don't yet
                                know who'll answer (avoids "Finn tänker…" when another agent was addressed). */}
                            <div className="finn-typing-dots">
                                <div className="finn-typing-dot"></div>
                                <div className="finn-typing-dot"></div>
                                <div className="finn-typing-dot"></div>
                            </div>
                            {/* What the team is doing, as it happens. Completed steps stay visible and
                                dim so the user can see the path the answer took; only the newest is
                                active. It is work, not reasoning — the labels come from tool NAMES on
                                the server (chat/activityLabels.ts) and never from their arguments. */}
                            {activity.length > 0 && (
                                <div className="finn-activity" aria-live="polite">
                                    {activity.map((step, i) => (
                                        <div
                                            key={step.step}
                                            className={`finn-activity-step${i === activity.length - 1 ? ' is-current' : ''}`}
                                        >
                                            <span className="finn-activity-bullet" />
                                            <span>{step.label}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    <div ref={messagesEndRef} />
                </div>
            </div>

            {/* Input area */}
            <div className="finn-input-area">
                <div className="finn-input-column">
                    {/* Attachments */}
                    {attachments.length > 0 && (
                        <div className="finn-attachments">
                            {attachments.map((att) => (
                                <div key={att.id} className="finn-attachment-chip">
                                    <FileText size={12} />
                                    <span>{att.name}</span>
                                    <span style={{ color: '#9ca3af' }}>({formatFileSize(att.size)})</span>
                                    <button className="finn-attachment-remove" onClick={() => removeAttachment(att.id)}>×</button>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* The session's task-context delegation expired and could not be refreshed.
                        Shown only when there is something to lose by it: with no transaction
                        attached the chat is entirely unaffected, and a banner would be noise. */}
                    {delegationExpired && taskContext && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 12px', margin: '0 0 8px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 10, fontSize: '0.8rem', color: '#92400e' }}>
                        <span>
                          Din session har gått ut, så jag kan inte läsa den valda posten.
                          Ladda om sidan för att fortsätta med den — eller ta bort kopplingen och fråga ändå.
                        </span>
                        <button
                          type="button"
                          onClick={() => window.location.reload()}
                          style={{ marginLeft: 'auto', background: 'none', border: 'none', color: '#92400e', fontSize: '0.78rem', cursor: 'pointer', fontWeight: 600, whiteSpace: 'nowrap' }}
                        >
                          Ladda om
                        </button>
                      </div>
                    )}

                    {/* WHICH transaction the assistant's context refers to. Shown because the
                        alternative — an assistant that silently knows about a row the user cannot
                        see named — makes the answer's scope unguessable, and because attaching
                        context the user did not intend should be visible and removable in one
                        click. The label is display text the bank wrote; it is never sent back. */}
                    {taskContext && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 12px', margin: '0 0 8px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10, fontSize: '0.8rem', color: '#374151' }}>
                        <Sparkles size={14} style={{ color: 'var(--primary)', flexShrink: 0 }} />
                        <span>
                          Frågar om: <strong>{taskContext.label || 'vald post'}</strong>
                        </span>
                        {taskReturnRef.current && <Link to={taskReturnRef.current}>Tillbaka till underlaget</Link>}
                        <button
                          type="button"
                          onClick={() => setTaskContext(null)}
                          aria-label="Ta bort kopplingen till den valda posten"
                          style={{ marginLeft: 'auto', background: 'none', border: 'none', color: '#6b7280', fontSize: '0.78rem', cursor: 'pointer', fontWeight: 500 }}
                        >
                          Ta bort
                        </button>
                      </div>
                    )}

                    {activeAgent && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 12px', margin: '0 0 8px', background: '#f0f4ff', border: '1px solid #dbe3ff', borderRadius: 10, fontSize: '0.8rem', color: '#4b5563' }}>
                        <div style={{ width: 22, height: 22, borderRadius: 6, overflow: 'hidden', flexShrink: 0 }}><BotAvatar agent={activeAgent} /></div>
                        <span>Du pratar med <strong>{activeAgent.displayName}</strong></span>
                        <button
                          type="button"
                          onClick={() => { setInputMessage((p) => (/^@finn\b/i.test(p) ? p : `@finn ${p}`)); textareaRef.current?.focus(); }}
                          style={{ marginLeft: 'auto', background: 'none', border: 'none', color: 'var(--primary)', fontSize: '0.78rem', cursor: 'pointer', fontWeight: 500 }}
                        >
                          ← Tillbaka till Finn
                        </button>
                      </div>
                    )}
                    <div style={{ position: 'relative' }}>
                    {mention.open && mention.items.length > 0 && (
                      <div style={{ position: 'absolute', left: 0, right: 0, bottom: '100%', marginBottom: 8, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,0.10)', overflow: 'hidden', zIndex: 20, maxHeight: 240, overflowY: 'auto' }}>
                        <div style={{ padding: '6px 12px', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.04em', color: '#9ca3af', borderBottom: '1px solid #f3f4f6' }}>Vem vill du prata med?</div>
                        {mention.items.map((a, i) => (
                          <div
                            key={a.handle}
                            onMouseDown={(e) => { e.preventDefault(); selectMention(a); }}
                            onMouseEnter={() => setMention((s) => ({ ...s, index: i }))}
                            style={{ display: 'flex', alignItems: 'baseline', gap: 8, padding: '8px 12px', cursor: 'pointer', background: i === mention.index ? '#f0f4ff' : '#fff' }}
                          >
                            <span style={{ fontWeight: 600, color: 'var(--primary)' }}>{a.handle}</span>
                            <span style={{ fontSize: '0.85rem' }}>{a.displayName}</span>
                            {a.role && <span style={{ fontSize: '0.78rem', color: '#6b7280', marginLeft: 'auto' }}>{a.role}</span>}
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="finn-input-row">
                        <div className="finn-input-box">
                            <div className="finn-textarea-wrap">
                                <div className="finn-input-highlight" ref={highlightRef} aria-hidden="true">
                                    {renderInlineMentions(inputMessage, agentRoster)}
                                </div>
                                <textarea
                                    ref={textareaRef}
                                    className="finn-textarea"
                                    value={inputMessage}
                                    onChange={handleInputChange}
                                    onKeyDown={handleKeyDown}
                                    onScroll={syncHighlightScroll}
                                    placeholder="Skriv ett meddelande… eller @ för att nå en specialist"
                                    disabled={!isConnected}
                                    rows={1}
                                />
                            </div>
                            <div className="finn-input-controls">
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    multiple
                                    onChange={handleFileUpload}
                                    style={{ display: 'none' }}
                                    accept="image/*,.pdf"
                                />
                                {agentRoster.length > 0 && (
                                    <button
                                        className="finn-input-icon-btn"
                                        onClick={openMentionMenu}
                                        disabled={!isConnected}
                                        title="Prata med en specialist"
                                        aria-label="Prata med en specialist"
                                    >
                                        <AtSign size={16} />
                                    </button>
                                )}
                                <button
                                    className="finn-input-icon-btn"
                                    onClick={() => fileInputRef.current?.click()}
                                    disabled={!isConnected}
                                >
                                    <Paperclip size={16} />
                                </button>
                            </div>
                        </div>
                        <button
                            className="finn-send-btn"
                            onClick={sendMessage}
                            disabled={(!inputMessage.trim() && attachments.length === 0) || !isConnected}
                        >
                            <Send size={18} />
                        </button>
                    </div>
                    </div>

                    <div className="finn-disclaimer">
                        Finn kan göra misstag — kontrollera viktiga uppgifter
                    </div>
                </div>
            </div>

            </div>

        </div>
    );
};

export default BookkeepingChatPage;
